import React, {useEffect, useRef, useState} from 'react';
import QRCodeStyling, {TypeNumber} from 'qr-code-styling';

export interface QRCodeGradient {
  type: 'linear' | 'radial';
  rotation: number;
  colorStops: Array<{
    offset: number;
    color: string;
  }>;
}

export interface QRCodeDotsOptions {
  type?:
    | 'square'
    | 'dots'
    | 'rounded'
    | 'classy'
    | 'classy-rounded'
    | 'extra-rounded';
  color?: string;
  gradient?: QRCodeGradient;
}

export interface QRCodeCornersSquareOptions {
  type?: 'square' | 'extra-rounded' | 'dot';
  color?: string;
  gradient?: QRCodeGradient;
}

export interface QRCodeCornersDotOptions {
  type?: 'square' | 'dot';
  color?: string;
  gradient?: QRCodeGradient;
}

export interface QRCodeImageOptions {
  hideBackgroundDots?: boolean;
  imageSize?: number;
  margin?: number;
  crossOrigin?: string;
}

export interface QRCodeBackgroundOptions {
  color?: string;
  gradient?: QRCodeGradient;
}

export interface QRCodeProps {
  // Required props
  data: string;

  // Size props
  width?: number;
  height?: number;
  margin?: number;

  // Type props
  type?: 'canvas' | 'svg';

  // QR Options
  qrOptions?: {
    typeNumber?: TypeNumber | undefined;
    mode?: 'Numeric' | 'Alphanumeric' | 'Byte' | 'Kanji';
    errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
  };

  // Styling options
  dotsOptions?: QRCodeDotsOptions;
  cornersSquareOptions?: QRCodeCornersSquareOptions;
  cornersDotOptions?: QRCodeCornersDotOptions;
  backgroundOptions?: QRCodeBackgroundOptions;
  imageOptions?: QRCodeImageOptions;

  // Image props
  image?: string;

  // Container props
  className?: string;
  style?: React.CSSProperties;

  // Event handlers
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

export function QRCode({
  data,
  width = 200,
  height = 200,
  margin = 0,
  type = 'canvas',
  qrOptions = {
    typeNumber: 0,
    mode: 'Byte',
    errorCorrectionLevel: 'L',
  },
  dotsOptions = {
    type: 'rounded',
    color: '#000000',
  },
  cornersSquareOptions = {
    type: 'extra-rounded',
    color: '#000000',
  },
  cornersDotOptions = {
    type: 'dot',
    color: '#000000',
  },
  backgroundOptions = {
    color: '#FFFFFF',
  },
  imageOptions = {
    hideBackgroundDots: true,
    imageSize: 0.4,
    margin: 0,
  },
  image,
  className = '',
  style = {},
  onLoad,
  onError,
}: QRCodeProps) {
  const [error, setError] = useState<Error | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const qrCodeRef = useRef<QRCodeStyling | null>(null);

  useEffect(() => {
    if (!data) {
      const error = new Error('QR code data is required');
      setError(error);
      onError?.(error);
      return;
    }

    try {
      // Create QR code styling instance
      const qrCodeStyling = new QRCodeStyling({
        width,
        height,
        margin,
        type,
        data,
        qrOptions,
        imageOptions,
        dotsOptions,
        backgroundOptions,
        cornersSquareOptions,
        cornersDotOptions,
        image,
      });

      qrCodeRef.current = qrCodeStyling;
      setError(null);

      // Render the QR code
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
        qrCodeStyling.append(containerRef.current);

        // Call onLoad after a short delay
        if (onLoad) {
          setTimeout(onLoad, 100);
        }
      }
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to create QR code');
      setError(error);
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, width, height, margin, type, image]);

  if (error) {
    return (
      <div
        className={`qr-code-error ${className}`}
        style={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          color: '#666',
          fontSize: '12px',
          ...style,
        }}>
        Failed to generate QR code
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`qr-code-container ${className}`}
      style={{
        width,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    />
  );
}

// Export default for easier imports
export default QRCode;
